import type { DatabaseMetadata, QuoteSummary } from '@shared/types';
import { isQuoteSnapshot } from '@shared/quote-snapshot-identity';

export { isQuoteSnapshot } from '@shared/quote-snapshot-identity';

export type DatabaseOpenTarget =
  | { kind: 'database' }
  | { kind: 'quote'; slot: number }
  | { kind: 'readonly-snapshot'; reason: 'missing' | 'ambiguous' };

function normalizedNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isValidQuoteSlot(value: unknown): value is number {
  return Number.isSafeInteger(value)
    && Number(value) >= 1
    && Number(value) <= 10_000;
}

export function resolveDatabaseOpenTarget(
  database: DatabaseMetadata,
  quotes: readonly QuoteSummary[],
): DatabaseOpenTarget {
  if (!isQuoteSnapshot(database)) {
    return { kind: 'database' };
  }

  const projectNumber = normalizedNonEmptyString(
    database?.projectInfo?.projectNumber,
  );
  if (projectNumber === null || !Array.isArray(quotes)) {
    return { kind: 'readonly-snapshot', reason: 'missing' };
  }

  const matches = quotes.filter((quote) => {
    const offerNumber = normalizedNonEmptyString(quote?.offerNumber);
    return offerNumber !== null
      && offerNumber === projectNumber;
  });

  if (matches.length !== 1) {
    return {
      kind: 'readonly-snapshot',
      reason: matches.length === 0 ? 'missing' : 'ambiguous',
    };
  }

  return isValidQuoteSlot(matches[0].slot)
    ? { kind: 'quote', slot: matches[0].slot }
    : { kind: 'readonly-snapshot', reason: 'missing' };
}
